import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetCoverBackgroundsQuery } from '../../../redux/api/fymAPI';
import type { CoverBankBackground } from '../../../redux/api/fymAPI.types';
import { useUserToken } from '../../../redux/slices/appInfo/hooks';
import { useQueryParam } from '../../../utils/hooks/useQueryParam';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { PublicCoverEditor } from './PublicCoverEditor';
import { publicCoverMessages } from './publicCoverMessages';
import { coverCarryPath, isCoverMakerEmbed, leadTokenOf } from './publicCoverPath';
import { browserCoverStorage, LOCAL_CARRY, readPublicCover, writeCarry } from './publicCoverStore';
import { useEmbedHeight } from './useEmbedHeight';
import { useNoIndex } from './useNoIndex';
import { usePublicCoverSource } from './usePublicCoverSource';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import editorStyles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const NO_BACKGROUNDS: CoverBankBackground[] = [];

/**
 * The public cover maker: `/app/cover-maker` (owner decision D11; design cover-editor g). No
 * login. `?embed=1` is the tool inside the frame of music24.com/song-artwork-maker; `?design=<token>`
 * opens the saved design of the "Your cover is saved" email.
 *
 * A signed-in visitor of the full page has songs: the page sends them to the song chooser with
 * the design of this browser (or of the link), where "Use as cover" exists. The embed stays the
 * public tool for everybody: the website page is the same for every visitor.
 */
const PublicCoverMakerViewComponent: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { search } = useLocation();
  const isEmbed = isCoverMakerEmbed(search);
  const token = leadTokenOf(useQueryParam('design', undefined, ''));
  const isSignedIn = useUserToken() !== null;
  const goesToSongs = isSignedIn && !isEmbed;
  const bank = useGetCoverBackgroundsQuery(undefined, { skip: goesToSongs });
  useNoIndex();
  useEmbedHeight(isEmbed);

  useEffect(() => {
    if (!goesToSongs) {
      return;
    }
    const storage = browserCoverStorage();
    if (token !== null) {
      writeCarry(storage, token);
    } else if (readPublicCover(storage) !== null) {
      writeCarry(storage, LOCAL_CARRY);
    }
    // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
    void navigate(coverCarryPath(), { replace: true });
  }, [goesToSongs, navigate, token]);

  const placeholders = useMemo(
    () => ({
      title: intl.formatMessage(publicCoverMessages.titleLabel),
      artist: intl.formatMessage(publicCoverMessages.artistLabel),
    }),
    [intl]
  );
  // A bank that does not load is an empty bank: the tool still opens on a colour.
  const backgrounds = bank.data?.backgrounds ?? (bank.isError ? NO_BACKGROUNDS : undefined);
  const start = usePublicCoverSource({ backgrounds: goesToSongs ? undefined : backgrounds, token, placeholders });

  // `data-fullscreen-editor`: the page under the tool gives back its scrollbar room (appStyle.scss).
  // Without it the tool is narrower than the window, and the frame of the website shows a band.
  return (
    <div className={editorStyles.screen} data-fullscreen-editor>
      {start === null || backgrounds === undefined ? (
        <div className={editorStyles.fullState} role="status">
          <LoadingView />
        </div>
      ) : (
        <PublicCoverEditor
          initial={start.cover}
          initialNote={start.note}
          backgrounds={backgrounds}
          isEmbed={isEmbed}
          isSignedIn={isSignedIn}
          placeholders={placeholders}
        />
      )}
    </div>
  );
};

export const PublicCoverMakerView = TypedMemo(PublicCoverMakerViewComponent);
